import cx from 'classnames';
import parse from 'html-react-parser';
import { useAtom } from 'jotai';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import Link from 'next/link';
import React, { FunctionComponent, ReactNode } from 'react';
import { renderToString } from 'react-dom/server';

import useLinkProps from '../../../hooks/use-link-props';
import { phones, emails } from '../../../shared';
import { openCookieConcent } from '../../../state/application';
import Breadcrumbs from '../../modules/breadcrumbs';
import PhoneNotice from '../../modules/help-contacts/phone-notice';
import FeatureLink from '../../ui/links/featured';
import PageTitle from '../../ui/typography/page-title';

import TermsSchema from './seo';
import classes from './terms.module.css';
import TermList, { TTermsItem } from './terms_list';

export type TTermsContent = {
  key: string;
  title: string;
  styleType: string | 'decimal';
  description?: string;
  items?: Array<TTermsItem>;
};

const TermsPage: FunctionComponent<TPageTerms> = ({}) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const privacy_link = renderToString(
    <Link
      {...getLink('terms-and-conditions', undefined, t('terms:hash_privacy'))}
      passHref
      legacyBehavior
    >
      <FeatureLink className="leading-7">
        {t('terms:terms.privacy_link')}
      </FeatureLink>
    </Link>
  );
  const cookies_link = renderToString(
    <Link
      {...getLink('terms-and-conditions', undefined, t('terms:hash_cookies'))}
      passHref
      legacyBehavior
    >
      <FeatureLink className="leading-7">
        {t('terms:terms.cookies_link')}
      </FeatureLink>
    </Link>
  );
  const email_link = renderToString(
    <Link href={`mailto:${emails.pt}`} passHref>
      {emails.pt}
    </Link>
  );
  const phone_link = renderToString(
    <>
      <Link href={`tel:${phones.pt}`} passHref>
        {phones.pt}
      </Link>
      <span className="block mt-1 text-xs ">
        {t('common:call-cost')}
        <br />
        {t('common:communication-cost')}
      </span>
    </>
  );
  const doc_form_link = renderToString(
    <Link
      href="https://bparts-eu.s3-eu-west-1.amazonaws.com/legal/return-form/pt.docx"
      passHref
      legacyBehavior
    >
      <FeatureLink className="leading-7" target="_blank">
        {t('terms:terms.doc_form_link')}
      </FeatureLink>
    </Link>
  );
  const contacts_link = renderToString(
    <Link {...getLink('contacts', undefined)} passHref legacyBehavior>
      <FeatureLink className="leading-7">
        {t('terms:terms.contacts_link')}
      </FeatureLink>
    </Link>
  );

  const country_currency = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk')
    ? 'GBP'
    : 'EUR';

  const contentMessages = t(
    'terms:terms.content',
    {
      privacy_link,
      cookies_link,
      email_link,
      phone_link,
      doc_form_link,
      contacts_link,
      country_currency,
    },
    {
      returnObjects: true,
    }
  ) as Array<TTermsContent>;

  const content = [...contentMessages];

  // find key terms:hash_cookies
  let new_cookies: Array<{
    description: ReactNode;
  }> = [
    {
      description:
        t('cookie-concent:required-label') +
        ': ' +
        t('cookie-concent:required-text'),
    },
    {
      description:
        t('cookie-concent:preferences-label') +
        ': ' +
        t('cookie-concent:preferences-text'),
    },
    {
      description:
        t('cookie-concent:statistics-label') +
        ': ' +
        t('cookie-concent:statistics-text'),
    },
    {
      description:
        t('cookie-concent:markting-label') +
        ':' +
        t('cookie-concent:markting-text'),
    },
    {
      description: '{{openCookieModal}}',
    },
  ];

  content[content.length - 1].items[1].items = new_cookies;

  return (
    <main>
      <TermsSchema breadcrumbs={[]} />
      <Breadcrumbs items={[]} currentPage={t('common:terms')} />
      <div className="container grid m-auto my-4 px-4 lg:my-12">
        <PageTitle title={t('common:terms')} />
        <div className="text-sm text-justify mb-2">
          <Trans
            i18nKey="terms:description"
            components={{ p: <p />, b: <b /> }}
          />
        </div>
        {content.map((item, index, arr) => (
          <section
            key={item.key}
            id={item.key}
            className={cx(
              arr.length - 1 !== index ? 'separator-bottom' : '',
              'lg:my-6',
              'mt-12'
            )}
          >
            <h2 className="font-bold text-xl  mb-4">{item.title}</h2>
            <div
              className={cx(
                classes.description,
                'text-sm',
                '',
                'text-justify',
                { 'mb-12': index + 1 < content.length }
              )}
            >
              {item.description && parse(item.description)}
              <TermList
                items={item.items}
                primary={true}
                styleType={item.styleType}
              ></TermList>
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default TermsPage;
